import { useCallback, useEffect, useRef, useState } from "react";
import { HANGER_HISTORY_LIMIT } from "../../Components/Device/Tabs/maintenance/hangerSensorHistory";

// Sozlesme: BayinerIOT-Workspace- docs/api-contracts/hanger-sensor-live.md (#42)
// Tablet, cekmece acikken ya da panel "Askı izlemeyi başlat" dediginde (v1.1)
// Operasyon Modu aciksa saniyede 1 yayin yapar;
// bu surede mesaj gelmezse ekran kapali sayilir.
const STALE_MS = 5000;

/**
 * Askı sensoru canli izleme. `ready` (authenticated) olunca
 * `hangerSensor:watch`, ayrilirken `hangerSensor:unwatch`.
 *
 * @returns {{ status: "connecting"|"watching"|"error", error: string|null,
 *   live: object|null, receivedAt: number|null, closedReason: string|null,
 *   history: { receivedAt: number, data: object }[] }}
 */
export default function useHangerSensorLive(socket, ready, serialNo) {
  const [status, setStatus] = useState("connecting");
  const [error, setError] = useState(null);
  const [live, setLive] = useState(null);
  const [receivedAt, setReceivedAt] = useState(null);
  const [closedReason, setClosedReason] = useState(null);
  // Son okumalar (eskiden yeniye) — Bakım sekmesinde dosya olarak indirilir.
  // Yayın durunca silinmez; yalnız cihaz değişince sıfırlanır.
  const [history, setHistory] = useState([]);
  const [, forceTick] = useState(0);
  // Uzaktan oturum (v1.1): panel "Askı izlemeyi başlat" dediyse seçili göz.
  const [remoteCup, setRemoteCup] = useState(null);
  const [remoteError, setRemoteError] = useState(null);
  const remoteCupRef = useRef(null);
  remoteCupRef.current = remoteCup;

  useEffect(() => {
    if (!socket || !serialNo) return undefined;
    const onUpdate = (msg) => {
      if (!msg || msg.serialNo !== serialNo || !msg.data) return;
      if (msg.data.active === false) {
        setLive(null);
        setReceivedAt(null);
        setClosedReason(msg.data.reason || "closed");
        return;
      }
      const at = Date.now();
      setLive(msg.data);
      setReceivedAt(at);
      setHistory((prev) =>
        [...prev, { receivedAt: at, data: msg.data }].slice(
          -HANGER_HISTORY_LIMIT,
        ),
      );
      setClosedReason(null);
    };
    socket.on("hangerSensor:update", onUpdate);
    return () => socket.off("hangerSensor:update", onUpdate);
  }, [socket, serialNo]);

  useEffect(() => {
    if (!socket || !serialNo) return undefined;
    if (!ready) {
      setStatus("connecting");
      return undefined;
    }
    // Yeniden baglanmada ready yeniden true olur — izleme yenilenir.
    socket.emit("hangerSensor:watch", { serialNo }, (ack) => {
      if (ack?.ok) {
        setStatus("watching");
        setError(null);
        // Yeniden bağlanmada uzaktan oturum da yenilensin
        if (remoteCupRef.current) {
          socket.emit(
            "hangerSensor:start",
            { serialNo, cup: remoteCupRef.current },
            () => {},
          );
        }
      } else {
        setStatus("error");
        setError(ack?.reason || "error");
      }
    });
    return () => {
      try {
        socket.emit("hangerSensor:unwatch", { serialNo });
      } catch (_) {}
    };
  }, [socket, ready, serialNo]);

  // Bayatlik kontrolu icin saniyede bir yeniden ciz (yalniz veri varken).
  useEffect(() => {
    if (!receivedAt) return undefined;
    const t = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [receivedAt]);

  useEffect(() => {
    setRemoteCup(null);
    setRemoteError(null);
    setHistory([]);
  }, [serialNo]);

  const start = useCallback(
    (cup) => {
      if (!socket || !ready || !serialNo) return;
      setRemoteError(null);
      socket.emit("hangerSensor:start", { serialNo, cup }, (ack) => {
        if (ack?.ok) setRemoteCup(cup);
        else setRemoteError(ack?.reason || "error");
      });
    },
    [socket, ready, serialNo],
  );

  const stop = useCallback(() => {
    setRemoteCup(null);
    if (socket && serialNo)
      socket.emit("hangerSensor:stop", { serialNo }, () => {});
  }, [socket, serialNo]);

  const isFresh = receivedAt != null && Date.now() - receivedAt < STALE_MS;

  return {
    status,
    error,
    live: isFresh ? live : null,
    receivedAt,
    closedReason,
    remoteCup,
    remoteError,
    history,
    start,
    stop,
  };
}
